<template>
  <div class="order-error" v-if="errorText" v-html="errorText" />
  <div class="order-card-wrap" v-else>
    <div
      :class="['order-card', {
        'order-card-collapsed': !isExpanded,
        'order-card-last': isLast,
        'order-card-expanded-last': isExpanded && isLast,
      }]"
    >
      <div class="order-card-head" @click="handleToggle">
        <div class="number">
          Заказ № {{ order.orderNumber }}
          <span :class="['order-card__status', {'order-card__status_warning': warnStatus }]">{{ status }}</span>
        </div>
        <div class="order-card-head-buttons">
          <ui-button
            v-if="prepayOpportunity && order.subtotal"
            class="button"
            :text="`Оплатить ${$options.filters.price(order.subtotal)} ₽`"
            @click.stop="pay"
          />
          <vue-client-only>
            <div
              v-if="adaptive.isDesktop"
              class="print-button"
              @click.stop="print"
            >
              <f-sprite-icon :icon-link="$options.icons.IconPrint.id" />
            </div>
          </vue-client-only>
          <div class="order-card-toggle-arrow">
            <chevron-animated
              :reverse="isExpanded"
            />
          </div>
        </div>
      </div>

      <template v-if="isExpanded">
        <div class="order-card-info">
          <div class="name">
            Получатель
            <span>{{ fullName }}</span>
          </div>

          <div class="phone">
            Телефон
            <span>{{ phone | prettyPhone }}</span>
          </div>

          <div class="delivery">
            Способ доставки
            <span>{{ delivery }}</span>
          </div>

          <div class="payment">
            Способ оплаты
            <span>{{ payment }}</span>
          </div>

          <div :class="['payment-status', {paid: isOrderPaid}]">
            Статус оплаты
            <div>
              <div v-if="isOrderPaid" class="payment-status__icon">
                <f-sprite-icon :icon-link="$options.icons.IconSuccess.id" />
              </div>
              <span>{{ paymentStatus }}</span>
            </div>
          </div>

          <div class="date">
            <template v-if="order.shippingGroupType === 'delivery'">
              Дата доставки
              <span>
                <template v-if="order.receivingDateFrom && order.receivingDateTo">
                  {{ getIntervalDates(order.receivingDateFrom, order.receivingDateTo) }} {{ order.deliveryTime ? `c ${order.deliveryTime}` : '' }}
                </template>
                <template v-else>
                  {{ order.receivingDateFrom | fullDate }} {{ order.deliveryTime ? `c ${order.deliveryTime}` : '' }}
                </template>
              </span>
            </template>
            <template v-else>
              Можно забрать
              <span>
                <template v-if="order.receivingDateTo && order.shippingGroupType === 'thirdparty'">
                  {{ getIntervalDates(order.receivingDateFrom, order.receivingDateTo) }}
                </template>
                <template v-else>
                  {{ order.receivingDateFrom | fullDate }}
                </template>
              </span>
            </template>
          </div>

          <div class="address">
            <template v-if="order.shippingGroupType === 'delivery'">
              Адрес доставки
            </template>
            <template v-else>
              Адрес
            </template>
            <span>{{ order.address }}</span>
          </div>

          <div v-if="schedule" class="schedule">
            Часы работы
            <span>{{ order.workSchedule[0].workTime }}</span>
          </div>

          <div
            v-if="order.storagePeriod && order.shippingGroupType === 'thirdparty'"
            class="storage"
          >
            Срок хранения:
            <span>
              {{ order.storagePeriod }} {{ order.storagePeriod | decl('д', ['ень', 'ня', 'ней']) }}
            </span>
          </div>

          <div
            v-if="order.drivingDirections"
            class="hint"
          >
            Как добраться
            <span>{{ order.drivingDirections }}</span>
          </div>

          <div
            v-if="order.insideLocation"
            class="hint"
          >
            Расположение
            <span>{{ order.insideLocation }}</span>
          </div>

          <div
            v-if="order.comment"
            class="comment"
          >
            Комментарий
            <span>{{ order.comment }}</span>
          </div>
        </div>

        <div class="order-card-products">
          <checkout-product
            v-for="(product, i) in order.products"
            :key="i"
            :product="product"
          />
        </div>

        <div class="dashed-table prices">
          <div>
            <span>{{ order.quantity }} {{ order.quantity | decl('товар', ['', 'а', 'ов']) }} на сумму</span>
            <span class="price-rub">{{ order.catalogAmount || 0 | price }}</span>
          </div>
          <div>
            <span>Стоимость доставки</span>
            <span
              v-if="order.deliveryAmount"
              class="price-rub"
            >
              {{ order.deliveryAmount | price }}
            </span>
          </div>
          <div
            v-if="Number(order.discountsAmount)"
            class="discount accent"
          >
            <span>Скидка</span>
            <span class="price-rub">
              - {{ order.discountsAmount | price }}
            </span>
          </div>
          <div class="large">
            <span>Всего к оплате</span>
            <span class="price-rub">{{ order.subtotal || 0 | price }}</span>
          </div>
        </div>

        <div class="order-buttons">
          <span class="link-blue" @click="$emit('on-close')">
            К списку заказов
          </span>
          <div class="buttons">
            <ui-button
              v-if="prepayOpportunity && order.subtotal"
              wide
              :text="`Оплатить ${$options.filters.price(order.subtotal)} ₽`"
              @click="pay"
            />

            <ui-button
              v-if="order.cancellation === 'available'"
              wide
              secondary
              text="Отменить заказ"
              @click="loadCancelReasons"
            />
          </div>
        </div>
      </template>

      <div
        v-if="!isLoaded"
        class="loader"
      >
        <transition name="fade">
          <ui-loader
            v-if="!isLoaded"
            size="60"
          />
        </transition>
      </div>
    </div>

    <order-support v-if="showSupportInfo" class="hint order-card-support">
      С вопросами обращайтесь в службу поддержки пользователей FUNDAY по почте  <a href="mailto:helpme@fundayshop.com">helpme@fundayshop.com</a> или по телефону <a href="tel:+78007700101">8 800 770-01-01</a> (по России бесплатно), с 7:00 до 22:00 (мск)
    </order-support>

    <modal
      :visible="cancelCard"
      @on-close="cancelCard = false"
    >
      <cancel-card
        :order-number="orderNumber"
        :reasons="reasons"
        @on-close="cancelCard = false"
        @on-order-update="updateOrder"
      />
    </modal>

    <fast-payment-system-modal
      v-model="isFpsPaymentVisible"
      :data="fpsPaymentData"
    />

    <payment-methods-modal
      v-model="isPaymentMethodsVisible"
      :payment-methods="paymentMethods"
      :loading="isLoadingPayment"
      @select="onSelectPaymentMethod"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import VueClientOnly from 'vue-client-only';
import { createOrdersSessionStorage } from '@/services/storage/orders-storage';
import { ACTIVE_PAYMENT_METHODS, PAYMENT_METHODS } from '@/constants/payment';
import { typGtmEvents } from '@/gtm/typ-gtm-events';
import { cartGtmEvents } from '@/gtm/cart-gtm-events';

/* Components */
import CheckoutProduct from '@/components/checkout/checkout-product/checkout-product';
import OrderSupport from '@/components/account/order-support.vue';
import CancelCard from '@/components/account/cancel-card.vue';
import ChevronAnimated from '@icons/chevron-animated';
import PaymentMethodsModal from '@/components/payment/payment-methods-modal/payment-methods-modal';
import FastPaymentSystemModal from '@/components/payment/fast-payment-system-modal/fast-payment-system-modal.vue';

/* Mixins */
import getIntervalDates from '@/mixins/get-interval-dates';

import IconSuccess from '@icons/circle-success.svg';
import IconPrint from '@icons/print.svg';

export default {
  name: 'OrderCard',

  components: {
    PaymentMethodsModal,
    FastPaymentSystemModal,
    CancelCard,
    OrderSupport,
    CheckoutProduct,
    ChevronAnimated,
    VueClientOnly,
  },

  mixins: [getIntervalDates],

  inject: ['adaptive'],

  props: {
    orderNumber: {
      type: String,
      required: true
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    showSupportInfo: {
      type: Boolean,
      default: false,
    },
    isExpandedDefault: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cancelCard: false,
      order: {},
      reasons: [],
      paymentTools: { tools: [] },
      isLoaded: false,
      errorText: '',
      isExpanded: this.isExpandedDefault,
      timerId: null,
      isFpsPaymentVisible: false,
      fpsPaymentData: null,
      isPaymentMethodsVisible: false,
      paymentMethods: [],
      isLoadingPayment: false,
    };
  },

  icons: {
    IconPrint,
    IconSuccess,
  },

  computed: {
    ...mapGetters({
      ACCOUNT: 'user/account'
    }),

    location() {
      return this.$route.name || '';
    },

    orderStatus() {
      switch (this.order.status) {
        case 'accepted':
          return 'В обработке';
        case 'performed':
          return 'Выполнен';
        case 'shipped_to_store':
          return 'Доставляется в магазин';
        case 'waited':
          return 'Ожидает в магазине';
        case 'shipped':
          return 'Доставляется';
        case 'prepared':
          return 'Собирается';
        case 'cancelled':
          return 'Отменён';
        default:
          return 'В обработке';
      }
    },

    TYPStatus() {
      if (this.needPrepay) {
        if (!this.isOrderPaid) {
          return 'Для обработки заказа требуется предоплата. Заказ будет автоматически отменён через 20 минут';
        } else if (this.isOrderPaid && this.isSelfPick) {
          return 'Мы пришлём SMS, когда товар будет готов к выдаче';
        } else if (this.isOrderPaid && !this.isSelfPick) {
          return 'Ваш заказ был подтверждён автоматически. Мы пришлём SMS с датой и временем доставки';
        }

        return '';
      } else {
        switch(this.order.shippingGroupType) {
          case 'delivery':
            return 'Ваш заказ был подтверждён автоматически. Мы пришлём SMS с датой и временем доставки';
          default:
            return 'Мы пришлём SMS, когда товар будет готов к выдаче';
        }
      }
    },

    status() {
      return this.location === 'TYP' ? this.TYPStatus : this.orderStatus;
    },

    payment() {
      switch (this.order.paymentMethod) {
        case PAYMENT_METHODS.cardOnline:
          return 'Онлайн оплата на сайте';
        case PAYMENT_METHODS.cashOrCard:
          return 'Наличными или картой при получении';
        case PAYMENT_METHODS.fps:
          return 'Система Быстрых Платежей';
        default:
          return 'Наличными при получении';
      }
    },

    paymentStatus() {
      return this.isOrderPaid ? 'Оплачен' : 'Не оплачен';
    },

    isSelfPick() {
      return this.order.shippingGroupType === 'pickup' || this.order.shippingGroupType === 'thirdparty';
    },

    delivery() {
      switch(this.order.shippingGroupType) {
        case 'pickup':
          return 'Самовывоз (Магазин FUNDAY)'
        case 'thirdparty':
          if (this.order.storeType === 'postamat') {
            return 'Самовывоз (Постамат)'
          } else if (this.order.storeType === 'pvz') {
            return 'Самовывоз (ПВЗ)'
          } else {
            return 'Самовывоз (Магазин FUNDAY)'
          }
        default:
          return 'Курьерская служба'
      }
    },

    fullName() {
      return this.order?.contact?.fullName || '';
    },

    phone() {
      return this.order?.contact?.phone || '';
    },

    needPrepay() {
      return this.order?.needPrepay;
    },

    schedule() {
      return this.order?.workSchedule?.[0]?.workTime || this.order?.workTime || '';
    },

    isOrderPaid() {
      return !this.paymentTools?.tools?.length && this.paymentTools?.totalPaid >= this.order?.subtotal;
    },

    prepayOpportunity() {
      const canPayOnline = [
        PAYMENT_METHODS.cardOnline,
        PAYMENT_METHODS.online,
        PAYMENT_METHODS.fps,
      ].some(i => this.paymentTools.tools.includes(i));

      return this.order.status !== 'cancelled'
          && this.order.status !== 'performed'
          && !this.isOrderPaid
          && canPayOnline;
    },

    warnStatus() {
      return this.location === 'TYP' && this.needPrepay && !this.isOrderPaid ;
    },

    activePaymentMethods() {
      const allowedMethods = [
        PAYMENT_METHODS.fps,
        PAYMENT_METHODS.cardOnline,
      ];

      return ACTIVE_PAYMENT_METHODS.reduce((res, method) => {
        if (allowedMethods.includes(method.value) && this.paymentTools.tools.includes(method.value)) {
          res[method.value] = method;
        }
        return res;
      }, {});
    },

    ordersStorage: () => createOrdersSessionStorage(),
  },

  watch: {
    isOrderPaid(val) {
      if (val) this.isExpanded = true;
    }
  },

  created() {
    if(!this.$isServer) {
      this.loadOrder(this.orderNumber);
    }
  },

  beforeDestroy() {
    clearTimeout(this.timerId);
  },

  methods: {
    ...mapActions({
      UPDATE_ORDER_STATUS: 'orders/updateOrderStatus',
      GET_PAYMENT_FPS_QR: 'cart/getPaymentFpsQr',
    }),

    print() {
      window.print();
    },

    async loadPaymentTools() {
      clearTimeout(this.timerId);

      const { orderNumber, status } = this.order;
      const fpsOrder = this.ordersStorage.ordersWithFps?.[orderNumber];

      const paymentTools = await this.$store.$api.getPaymentTools({
        orderNumber,
        intentionNumber: fpsOrder?.id,
      });

      this.paymentTools = paymentTools;

      if (!fpsOrder) return;

      // если заказ оплачен или статус заказа выполнен или отменен, то заказ считаем оплаченным и прекращаем обработку
      if (paymentTools.status === 'paid' || ['cancelled', 'performed'].includes(status)) {
        this.ordersStorage.removeOrderWithFps(orderNumber);
        typGtmEvents.paidFps({ orderNumber });
        return;
      }

      if (fpsOrder.startDate) {
        // после истечения 20 минут с момента первого запроса прекращаем обработку
        if (new Date() - fpsOrder.startDate > 20 * 60 * 1000) {
          this.ordersStorage.removeOrderWithFps(orderNumber);
          return;
        }
      } else {  // если первый запрос, ставим дату
        this.ordersStorage.updateOrderWithFps(orderNumber, {
          ...fpsOrder,
          startDate: +new Date(),
        });
      }

      // status не равен paid, то подождать 3 секунды после получения ответа, направить запрос повторно
      this.timerId = setTimeout(() => {
        this.loadPaymentTools().catch(() => { // если ошибка, то прекращаем запросы
          clearTimeout(this.timerId);
          this.ordersStorage.removeOrderWithFps(orderNumber);
        });
      }, 3000);
    },

    loadOrder(number) {
      let orderNumber = number;

      if (orderNumber.indexOf('/') !== -1) { // для мультизаказов
        orderNumber = orderNumber.replace('/', '%2F');
      }

      return this.$store.$api.getOrderDetails({orderNumbers: [orderNumber]})
        .then(orders => {
          this.order = orders[0];
          return this.loadPaymentTools();
        })
        .then(() => {
          this.$emit('order-data-loaded', this.order);
          this.isLoaded = true;
        })
        .catch((e) => {
          console.log(e)
          this.errorText = 'Ошибка загрузки информации о заказе. <br>К сожалению, не смогли подгрузить информацию о вашем заказе.<br>Извините за временные неудобства ';
          this.$store.dispatch('notifications/create', {
            title: 'Техническая проблема',
            message: 'Ошибка загрузки информации о заказе',
          });
          this.isLoaded = true;
          this.$emit('on-close');
        });
    },

    updateOrder() {
      this.isLoaded = false;
      this.loadOrder(this.orderNumber).then(() => {
        this.UPDATE_ORDER_STATUS(this.orderNumber);
      });
    },

    loadCancelReasons() {
      this.$store.$api.getCancelReasons()
        .then(r => {
          this.reasons = r.map(reason => ({
            label: reason.description,
            value: reason.code
          }));
          this.cancelCard = true;
        })
        .catch(() => {
          this.$store.dispatch('notifications/create', {
            title: 'Ошибка',
            message: 'Не удалось отменить заказ. Повторите запрос позже.'
          });
        });
    },

    async openFpsPaymentModal() {
      const { orderNumber } = this.order;

      let fpsOrder = this.ordersStorage.ordersWithFps[orderNumber];

      if (!fpsOrder) { // если нет данных для СБП оплаты, генерим новую ссылку
        const fpsOrders = await this.GET_PAYMENT_FPS_QR({ orderNumbers: [orderNumber] });
        fpsOrder = fpsOrders[orderNumber];
      }

      this.fpsPaymentData = {
        totalPrice: this.order.subtotal,
        qrUrl: fpsOrder.url,
        orderNumber,
      };

      this.isLoadingPayment = false;
      this.isPaymentMethodsVisible = false;
      await this.$nextTick();

      this.isFpsPaymentVisible = true;
      typGtmEvents.payFps({ orderNumber });

      this.loadPaymentTools();
    },

    openPaymentMethodsModal() {
      this.paymentMethods = Object.values(this.activePaymentMethods);
      this.isPaymentMethodsVisible = true;
    },

    openPaymentLink() {
      const { orderNumber } = this.order;
      typGtmEvents.payOnline({ orderNumber });
      this.$store.$api.getPaymentLink({ orderNumber })
        .then(({ url }) => {
          window.location = url;
        })
        .catch(() => {
          this.$store.dispatch('notifications/create', {
            title: 'Ошибка',
            message: 'Не удалось оплатить заказ. Повторите запрос позже.',
          });
        })
        .finally(() => {
          this.isLoadingPayment = false;
          this.isPaymentMethodsVisible = false;
        });
    },

    pay() {
      if (this.activePaymentMethods[PAYMENT_METHODS.cardOnline] && this.activePaymentMethods[PAYMENT_METHODS.fps]) {
        this.openPaymentMethodsModal();
      } else if (!this.activePaymentMethods[PAYMENT_METHODS.cardOnline]) {
        this.openFpsPaymentModal();
      } else if (!this.activePaymentMethods[PAYMENT_METHODS.fps]) {
        this.openPaymentLink();
      }
    },

    async onSelectPaymentMethod({ value }) {
      this.isLoadingPayment = true;

      cartGtmEvents.paymentMethodChanged({
        selectedMethod: value,
        availableMethods: this.paymentMethods,
      });

      switch (value) {
        case PAYMENT_METHODS.cardOnline:
          return this.openPaymentLink();
        case PAYMENT_METHODS.fps:
          return this.openFpsPaymentModal();
        default:
          this.isLoadingPayment = false;
          this.isPaymentMethodsVisible = false;
          break;
      }
    },

    handleToggle() {
      this.isExpanded = !this.isExpanded;
    },
  }
};
</script>

<style lang="scss">
.order-card {
  position: relative;
  border-top: 1px solid $gray_lu;
  padding: 0 16px;

  &-wrap {
    margin: 0 -16px;
  }

  &-last  {
    border-bottom: 1px solid $gray_lu;
  }

  &-head {
    padding: 24px 16px 16px 16px;
    margin: 0 -16px;
    cursor: pointer;
    display: flex;
    flex-direction: column;

    .number {
      display: flex;
      flex-direction: column;
      font-family: VAGRounded, sans-serif;
      font-size: 20px;
      line-height: 28px;

      span {
        font-size: 14px;
        line-height: 20px;
        font-family: Gilroy, sans-serif;
      }
    }

    .button {
      margin-top: 16px;
    }

    &-buttons {
      display: flex;
    }
  }

  &__status_warning {
    color: $red;
  }

  &-toggle-arrow {
    width: 15px;
    height: 15px;
    position: absolute;
    right: 16px;
    top: 26px;
    color: $gray_l;

    @include breakpoint("tablet") {
      position: static;
      height: 40px;
      margin-left: 14px;
      flex-shrink: 0;
      display: flex;
      margin-bottom: 4px;
    }

    @include breakpoint("desktop") {
      margin-top: 8px;
    }
  }

  &.order-card-expanded-last {
    border-bottom: 0;
  }

  &.order-card-collapsed.order-card-last {
    border-bottom: 1px solid $gray_lu;
  }

  &-support {
    margin-left: 16px;
    margin-right: 16px;
  }

  &-info {
    padding-bottom: 30px;
    border-top: 1px solid $gray_lu;
    border-bottom: 1px solid $gray_lu;

    & > div {
      display: flex;
      flex-direction: column;
      margin-top: 12px;
      font-size: 12px;
      line-height: 20px;

      span {
        font-size: 14px;
        line-height: 24px;
        font-weight: bold;
      }
    }

    .payment-status {
      span {
        color: $red;
        vertical-align: middle;
      }

      &.paid {
        span {
          color: $black;
        }
      }

      &__icon {
        width: 18px;
        height: 18px;
        color: $blue;
        display: inline-block;
        vertical-align: middle;
        margin-right: 6px;
      }
    }

    & > div:first-child {
      margin-top: 24px;
    }
  }

  &-products {
    .checkout-product {
      padding: 20px 0 24px 0;
    }
  }

  .prices {
    margin-top: 24px;

    .order-card-collapsed & {
      margin-bottom: 32px;
    }
  }

  .order-buttons {
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    margin-top: 24px;
    .thankyou-page & {
      display: none;
    }

    .link-blue {
      display: block;
      margin-top: 24px;
      font-size: 12px;
      line-height: 24px;
    }

    & > .buttons {
      width: 100%;

      & > button:last-of-type {
        margin-top: 8px;
      }
    }
  }

  & > .loader {
    position: absolute;
    top: -1px;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    color: $blue;
    background-color: #fff;

    & > .ui-loader {
      margin-top: 80px;
    }
  }

  .modal {
    & > .modal-content {
      padding: 40px 24px 24px;
    }
  }
}

.order-error {
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
  text-align: center;
}

@include breakpoint("tablet") {
  .order-card {
    &-head {
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
      padding-bottom: 24px;
      .thankyou-page & {
        padding-top: 24px;
      }

      .button {
        min-width: 150px;
        margin-top: 4px;
        margin-bottom: 4px;
      }
    }

    &-info {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      padding-bottom: 40px;
      border-bottom: 1px solid $gray_lu;

      & > div {
        margin-top: 20px;
      }

      .name {
        order: 1;
      }

      .delivery {
        order: 2;
      }

      .phone {
        order: 3;
      }

      .payment {
        order: 4;
      }

      .date {
        order: 5;
      }

      .payment-status {
        order: 6;
      }

      .address {
        order: 7;
      }

      .schedule {
        order: 8;
      }

      .storage {
        order: 9;
      }

      .hint {
        order: 10;
      }

      .comment {
        order: 11;
      }

      .delivery,
      .name,
      .payment,
      .date,
      .phone {
        width: 50%;
      }

      .address,
      .schedule,
      .storage,
      .hint,
      .comment {
        width: 100%;
      }
    }

    &-products {
      .checkout-product {
        padding: 32px 0;
      }
    }

    .prices {
      margin-top: 26px;
    }

    .order-buttons {
      flex-direction: row;
      margin-top: 32px;

      .link-blue {
        margin-top: 0;
        font-size: 14px;
      }

      & > .buttons {
        display: flex;
        width: auto;
        margin-left: auto;

        & > button {
          width: 150px;
        }

        & > button:last-of-type {
          margin-top: 0;
          margin-left: 16px;
        }
      }
    }

    .modal {
      & > .modal-content {
        width: 440px;
        height: auto;
        max-height: 100%;
        min-height: auto;
        padding: 48px 40px 36px;
        border-radius: 4px;
      }
    }
  }
}

@include breakpoint("desktop") {
  .order-card {
    &-wrap {
      width: 816px;
      margin: 0 auto;
    }

    &-head {
      align-items: flex-start;
      margin-top: 4px;
      padding-bottom: 32px;
      .thankyou-page & {
        padding-top: 32px;
      }

      .number {
        font-size: 32px;
        line-height: 48px;

        span {
          font-size: 16px;
          line-height: 24px;
        }
      }

      .button,
      .print-button {
        margin-top: 8px;
      }

      .button {
        margin-left: auto;
      }

      .print-button {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 4px;
        margin-left: 12px;
        width: 40px;
        height: 40px;
        padding: 8px;
        border: 1px solid $gray_lu;
        border-radius: 4px;
        cursor: pointer;
      }
    }

    &-info {
      padding-bottom: 56px;

      & > div {
        font-size: 14px;
        margin-top: 24px;

        span {
          font-size: 16px;
        }
      }

      .address,
      .schedule,
      .storage,
      .hint,
      .comment {
        margin-top: 32px;
      }
    }

    .prices {
      margin-top: 32px;
    }
  }

  .order-error {
    font-size: 20px;
    line-height: 28px;
  }
}

@media print {
  .order-card {
    .print-button,
    .ui-button,
    .order-buttons {
      display: none;
    }
    .checkout-product {
      break-inside: avoid;
    }
  }
}
</style>
